import { Redeem } from "@mui/icons-material";
import { useAccount, useWalletClient } from "wagmi";
import { createPublicClient, createWalletClient, custom, http } from "viem";

import { ListAction, ListActionVariant } from "@framework/styled";
import { type AddressType, type IStakingDeposit, StakingDepositStatus } from "@framework/types";
import { useMetamask } from "@dzambalaorg/react-hooks-eth";
import StakingReceiveRewardABI from "@framework/abis/json/Staking/receiveReward.json";
import { useGlobalDialogFormCallbacks } from "@framework/shared";
import { IOnConfirmArgs } from "@dzambalaorg/react-hooks";

import { IDepositRewardDto } from "../../../../dialogs/reward-dialog";
import { MarketDialogKeys } from "../../../../../shared";

export interface IStakingRewardComplexButtonProps {
  className?: string;
  disabled?: boolean;
  stake: IStakingDeposit;
  variant?: ListActionVariant;
}

export const StakingRewardComplexButton = (props: IStakingRewardComplexButtonProps) => {
  const { className, disabled, stake, variant } = props;

  const { openDialog } = useGlobalDialogFormCallbacks<IStakingDeposit, IDepositRewardDto>();

  const initialValues = {
    rule: stake.stakingRule!,
    startTimeStamp: stake.startTimestamp,
    withdrawDeposit: false,
    breakLastPeriod: false,
  };

  const { address, connector } = useAccount();
  const { data: client } = useWalletClient();

  const { execute: metaFn } = useMetamask(async (stake: IStakingDeposit, values: IDepositRewardDto) => {
    const provider = await connector!.getProvider();

    const publicClient = createPublicClient({
      chain: client!.chain,
      transport: http(client!.chain.rpcUrls.default.http[0], client!.transport),
      pollingInterval: 60_000,
    });

    const walletClient = createWalletClient({
      chain: client!.chain,
      transport: custom(provider as any),
    });

    // https://ethereum.stackexchange.com/questions/132850/incorrect-gaslimit-estimation-for-transaction
    const estimateGas = await publicClient.estimateContractGas({
      address: stake.stakingRule!.contract!.address as AddressType,
      abi: StakingReceiveRewardABI,
      functionName: "receiveReward",
      args: [stake.externalId, values.withdrawDeposit, values.breakLastPeriod],
      account: address!,
    });

    return await walletClient.writeContract({
      address: stake.stakingRule!.contract!.address as AddressType,
      abi: StakingReceiveRewardABI,
      functionName: "receiveReward",
      args: [stake.externalId, values.withdrawDeposit, values.breakLastPeriod],
      gas: (estimateGas * 110n) / 100n,
      account: address!,
    });
  });

  const handleRewardConfirm = ({ data }: IOnConfirmArgs<IDepositRewardDto>) => {
    return metaFn(stake, data);
  };

  const handleReward = (): void => {
    openDialog(MarketDialogKeys.STAKING_REWARD, stake, {
      initialValues,
      message: "dialogs.get-reward",
      testId: "DepositRewardDialogForm",
      onExternalConfirm: handleRewardConfirm,
      componentProps: {
        initialValues,
      },
    });
  };

  if (stake.stakingDepositStatus !== StakingDepositStatus.ACTIVE) {
    return null;
  }

  return (
    <ListAction
      onClick={handleReward}
      icon={Redeem}
      message="form.tips.reward"
      className={className}
      dataTestId="StakingRewardComplexButton"
      disabled={disabled}
      variant={variant}
    />
  );
};
